<template>
  <div
    id="venue-mapping-tool"
    ref="softjournWidgetRef"
    class="vmtAppModal hide"
  />
</template>

<script lang="ts">
export function buildRefreshCartMessage(): string {
  return JSON.stringify({
    jsonrpc: "2.0",
    method: "refreshCart",
    params: [],
  });
}
</script>

<script setup lang="ts">
import { useScriptTag } from "@vueuse/core";
import useStaticData from "@common/composables/useStaticData";

const props = withDefaults(
  defineProps<{
    mode: "edit" | "inventory" | "picker";
    venueId: number;
    vmtEventId: string;
    token: string;
    seatMapName?: string;
    reservationId?: string;
    showTicketTypesTab: boolean;
    sectionOnlySelection?: boolean;
  }>(),
  {
    showTicketTypesTab: true,
  },
);

const emit = defineEmits<{
  close: [];
  mapSaved: any;
  ticketTypeChange: any;
  inventoryChange: any;
  ticketsChange: any;
  seatStateChange: [
    data: { seatIds: string[]; state: "selected" | "deselected" },
  ];
}>();

const staticData = useStaticData();
const softjournWidgetRef = ref<HTMLDivElement | null>(null);

async function onVmtMapSaved(data: any) {
  emit("mapSaved", data);
}

async function onTicketTypeChange(_: any, data: any) {
  emit("ticketTypeChange", data);
}

async function onInventoryChange(data: any) {
  emit("inventoryChange", data);
}

async function onTicketsChange(data: any) {
  emit("ticketsChange", data);
}

async function onSeatStateChange(data: {
  seatIds: string[];
  state: "selected" | "deselected";
}) {
  emit("seatStateChange", data);
}

function getModeFeatureFlags() {
  if (props.mode === "picker") {
    return {
      addToCartBtn: false,
      checkoutBtn: false,
      closeWidgetOnCheckout: false,
    };
  }
  if (props.mode === "inventory") {
    return {
      addToCartBtn: false,
      checkoutBtn: false,
      closeWidgetOnCheckout: false,
    };
  }
  // edit
  return {
    addToCartBtn: false,
    checkoutBtn: true,
    closeWidgetOnCheckout: true,
  };
}

async function initSjVmtWidget() {
  const vmtWidget = window.VMTWidget?.default;
  const { addToCartBtn, checkoutBtn, closeWidgetOnCheckout } =
    getModeFeatureFlags();

  if (props.mode === "edit") {
    vmtWidget.onClose(() => emit("close"));
  }

  vmtWidget.method("onMapSaved", onVmtMapSaved);
  vmtWidget.method("onTicketTypeChange", onTicketTypeChange);
  vmtWidget.method("onInventoryChange", onInventoryChange);
  vmtWidget.method("onTicketsChange", onTicketsChange);
  vmtWidget.method("onSeatStateChange", onSeatStateChange);

  // socketUrl is intentionally omitted — the widget defaults to window.location.host,
  // which connects to our own socket.io-compatible WebSocket endpoint at /socket.io/.
  const launchOptions = {
    memberId: `${staticData.APP_TID}`,
    eventId: props.vmtEventId,
    venueId: `${props.venueId}`,
    mapName: `${props.seatMapName ?? ""}`,
    mode: props.mode,
    currency: "USD",
    priceFormat: "$0,0.00",
    tokenVersion: "new",
    frontPoint: `/vmt/index.html`,
    apiUrl: `/api/vmt`,
    hideCloseBtn: true,
    hideExpandBtn: true,
    featureFlags: {
      sectionOnlySelection:
        props.sectionOnlySelection ?? props.mode === "inventory",
      settingsTab: false,
      ranksTab: false,
      statsTab: false,
      ticketTypesTab: props.showTicketTypesTab,
      wholeTableBooking: true,
      orphanSeats: false,
      shoppingCart: true,
      pickerRowLabel: true,
      pickerSectionLabel: true,
      lockSoldSections: true,
      addToCartBtn,
      checkoutBtn,
      closeBtn: false,
      showTemplateSelector: false,
      singleClickToggle: true,
      showConvertToGaButton: false,
    },
    toolbox: {
      allowedTTEdit: false,
    },
    token: props.token,
    ...(props.reservationId ? { reservationId: props.reservationId } : {}),
    localization: "en",
    closeWidgetOnCheckout,
  };

  // The widget loader focuses the iframe the moment it appends it. Hosts rebuild the
  // widget while the user is working elsewhere on the page, a ticket price field
  // autosaving, for example, and that focus swallows whatever they type next. Hold the
  // focus where it is whenever someone is already using the page; on a fresh load
  // nothing is focused yet, so opening a map still leaves the canvas keyboard-ready.
  // launch() reaches the iframe synchronously, so the patch never outlives the call.
  const activeElement = document.activeElement;
  const holdHostFocus =
    !!activeElement &&
    activeElement !== document.body &&
    // Focus already on the old iframe means the user was on the map; let the rebuilt
    // one take it back.
    !softjournWidgetRef.value?.contains(activeElement);

  const originalIframeFocus = HTMLIFrameElement.prototype.focus;
  if (holdHostFocus) {
    HTMLIFrameElement.prototype.focus = function () {};
  }
  try {
    vmtWidget.launch(launchOptions);
  } finally {
    if (holdHostFocus) {
      HTMLIFrameElement.prototype.focus = originalIframeFocus;
    }
  }
}

const { load: loadSjVmtWidget } = useScriptTag(
  () => "/vmt/widget/bundle.js",
  () => initSjVmtWidget(),
  { manual: true },
);

watch(
  [softjournWidgetRef, () => props.vmtEventId, () => props.token],
  ([el, eventId, token]) => {
    if (!el || !eventId || !token) {
      return;
    }
    el.innerHTML = "";
    if (window.VMTWidget) {
      initSjVmtWidget();
    } else {
      loadSjVmtWidget();
    }
  },
);

async function refresh() {
  if (!softjournWidgetRef.value) return;
  softjournWidgetRef.value.innerHTML = "";
  await initSjVmtWidget();
}

function pushRefreshCartSignal() {
  const iframe = softjournWidgetRef.value?.querySelector("iframe");
  if (!iframe?.contentWindow) {
    console.warn(
      "sj-vmt-widget: cannot push refresh-cart signal, iframe not ready",
    );
    return;
  }
  iframe.contentWindow.postMessage(buildRefreshCartMessage(), "*");
}

defineExpose({ refresh, pushRefreshCartSignal });
</script>

<style>
.vmtAppModal {
  margin-top: 0 !important;
  border: 1px solid #c1c1c1;
  padding: 6px;
  border-radius: 2px;
  box-shadow:
    1px 1px 0 0 rgba(75, 75, 75, 0.15),
    inset 0 0 3px 0 #dbdbdb;
  background-color: white;
  height: 70vh;
}
</style>
